import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Empty, Form, Input, Modal, Popconfirm, Select, Skeleton, Space, Switch,
  Tag, Typography, message,
} from 'antd';
import type { CreateLeaveRequest, LeaveRequestRow, LeaveStatus, LeaveType } from '@velora/contracts';
import type { Dayjs } from 'dayjs';
import { ApiError, productApi } from '../../api';

/**
 * Teacher — My leaves. Mobile-first list of my own requests + balance strip
 * for the current session + submit modal.
 */
const TYPE_TONE: Record<LeaveType, string> = {
  casual: 'blue', sick: 'volcano', earned: 'green', unpaid: 'default', other: 'purple',
};
const STATUS_TONE: Record<LeaveStatus, string> = {
  pending: 'processing', approved: 'success', rejected: 'error', cancelled: 'default',
};

export function TeacherLeavesPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['my-leaves', institution],
    queryFn: () => productApi.getMyLeaves(institution),
    enabled: !!institution,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => productApi.cancelLeaveRequest(institution, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-leaves', institution] });
      void message.success('Cancelled');
    },
  });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (q.isError) {
    const b = q.error instanceof ApiError ? (q.error.body as { error?: string } | null) : null;
    if (b?.error === 'staff_not_linked') {
      return <Alert type="warning" showIcon message="Your account isn't linked to a staff record yet." description="Ask the admin to add your email to the staff directory." />;
    }
  }
  const requests = q.data?.requests ?? [];
  const balances = q.data?.balances ?? [];
  const session = q.data?.session ?? null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>My leaves</Typography.Title>
        <Button type="primary" onClick={() => setAddOpen(true)}>Request leave</Button>
      </div>

      {session ? (
        <Card size="small" title={<span style={{ fontSize: 12.5 }}>Balance · <b>{session.label}</b></span>}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
            {balances.map((b) => (
              <div key={b.leave_type} style={{ background: '#f7f7f5', borderRadius: 6, padding: '8px 10px' }}>
                <Tag color={TYPE_TONE[b.leave_type]} style={{ marginBottom: 4 }}>{b.leave_type}</Tag>
                <div style={{ fontSize: 15, fontWeight: 600 }}>
                  {b.remaining_days} <span style={{ color: '#8a94a6', fontSize: 11 }}>/ {b.allocated_days}</span>
                </div>
                <div style={{ fontSize: 11, color: '#8a94a6' }}>used {b.used_days}</div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Alert type="info" showIcon message="Balances appear once the admin sets a current session." />
      )}

      {requests.length === 0 ? (
        <Empty description="No leave requests yet." style={{ padding: 20 }} />
      ) : (
        requests.map((r) => <LeaveCard key={r.id} r={r} onCancel={() => cancel.mutate(r.id)} />)
      )}

      <SubmitModal open={addOpen} institution={institution} onClose={() => setAddOpen(false)} />
    </Space>
  );
}

function LeaveCard({ r, onCancel }: { r: LeaveRequestRow; onCancel: () => void }) {
  return (
    <Card size="small">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <Tag color={TYPE_TONE[r.leave_type]}>{r.leave_type}</Tag>
        <b>{r.from_date}</b> → <b>{r.to_date}</b> · {r.days}d
        {r.half_day && <Tag>half-day</Tag>}
        <div style={{ flex: 1 }} />
        <Tag color={STATUS_TONE[r.status]}>{r.status}</Tag>
      </div>
      <div style={{ marginTop: 6, fontSize: 12.5, color: '#3f4a63' }}>{r.reason}</div>
      {r.decision_note && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#553a9c' }}>
          <b>Note:</b> {r.decision_note}
        </div>
      )}
      {r.status === 'pending' && (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <Popconfirm title="Cancel this request?" onConfirm={onCancel}>
            <Button size="small" danger>Cancel</Button>
          </Popconfirm>
        </div>
      )}
    </Card>
  );
}

interface SubmitForm {
  leave_type: LeaveType;
  range: [Dayjs, Dayjs];
  half_day: boolean;
  reason: string;
}

function SubmitModal({
  open, institution, onClose,
}: { open: boolean; institution: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [form] = Form.useForm<SubmitForm>();
  const range = Form.useWatch('range', form);
  const from = range?.[0]?.format('YYYY-MM-DD');
  const to = range?.[1]?.format('YYYY-MM-DD');
  const consequence = useQuery({
    queryKey: ['leave-consequence', institution, from, to],
    queryFn: () => productApi.getLeaveConsequence(institution, from!, to!),
    enabled: open && !!institution && !!from && !!to,
  });

  const m = useMutation({
    mutationFn: (input: CreateLeaveRequest) => productApi.createLeaveRequest(institution, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-leaves', institution] });
      form.resetFields();
      void message.success('Submitted');
      onClose();
    },
  });
  const body = m.error instanceof ApiError ? (m.error.body as { message?: string } | null) : null;
  const err = body?.message ?? (m.error instanceof Error ? m.error.message : null);
  return (
    <Modal
      open={open}
      title="Request leave"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Submit"
      confirmLoading={m.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ leave_type: 'casual' as LeaveType, half_day: false }}
        onFinish={(v) => {
          const [f, t] = v.range;
          m.mutate({
            leave_type: v.leave_type,
            from_date: f.format('YYYY-MM-DD'),
            to_date: t.format('YYYY-MM-DD'),
            half_day: v.half_day,
            reason: v.reason.trim(),
          });
        }}
      >
        <Form.Item name="leave_type" label="Type" rules={[{ required: true }]}>
          <Select
            options={(['casual', 'sick', 'earned', 'unpaid', 'other'] as LeaveType[]).map((t) => ({
              value: t, label: t,
            }))}
          />
        </Form.Item>
        <Form.Item name="range" label="Dates" rules={[{ required: true, message: 'Pick a date range' }]}>
          <DatePicker.RangePicker style={{ width: '100%' }} />
        </Form.Item>

        {/* U-22: preview consequences before submit */}
        {consequence.data && (
          <ConsequencePreview data={consequence.data} />
        )}

        <Form.Item name="half_day" label="Half day" valuePropName="checked" tooltip="Deducts 0.5 from the total.">
          <Switch />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, min: 3, max: 500 }]}>
          <Input.TextArea rows={3} placeholder="Short reason your admin will see." />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function ConsequencePreview({
  data,
}: { data: import('@velora/contracts').LeaveConsequenceResponse }) {
  if (data.slots.length === 0) {
    return (
      <Alert
        type="success" showIcon
        style={{ marginBottom: 16 }}
        message="No periods scheduled in this range"
        description="Nothing on your timetable falls in the picked dates — no substitutions needed."
      />
    );
  }
  return (
    <div style={{ marginBottom: 16 }}>
      <Alert
        type={data.uncovered_count > 0 ? 'warning' : 'info'}
        showIcon
        message={
          data.uncovered_count > 0
            ? `${data.uncovered_count} of ${data.slots.length} period${data.slots.length === 1 ? '' : 's'} will need a substitution`
            : `All ${data.slots.length} periods already have a proxy arranged`
        }
        description="Admin can arrange substitutions after approval — this is a preview."
      />
      <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', fontSize: 12.5 }}>
        {data.slots.map((s, i) => (
          <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #f1f1f4', display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <b>{s.date}</b>
            <span>P{s.period}</span>
            <span>{s.subject_name}</span>
            <span style={{ color: '#8a94a6' }}>{s.grade}{s.section ? `-${s.section}` : ''}</span>
            <div style={{ flex: 1 }} />
            {s.covered
              ? <Tag color="success">covered · {s.proxy_teacher_name ?? '—'}</Tag>
              : <Tag color="warning">needs substitute</Tag>}
          </div>
        ))}
      </div>
    </div>
  );
}
